/**
 * Geohash Yardımcı Fonksiyonları
 * Geohash işlemleriyle ilgili yardımcı fonksiyonları içerir.
 */

import Geohash from "latlon-geohash";

/**
 * Mobil cihaz olup olmadığını tespit eder.
 * @returns {boolean} - Mobil cihaz ise true, değilse false
 */
export const isMobileDevice = () => {
  return window.innerWidth <= 768;
};

/**
 * Zoom seviyesine göre uygun precision değerini döndürür.
 * Mobil cihazlar için farklı precision algoritması kullanır.
 * @param {number} zoom - Harita zoom seviyesi
 * @param {boolean} isMobile - Mobil cihaz olup olmadığı (opsiyonel)
 * @returns {number} - Geohash precision değeri
 */
export const getPrecisionByZoom = (zoom, isMobile = null) => {
  // Eğer isMobile parametresi verilmemişse otomatik tespit et
  const mobile = isMobile !== null ? isMobile : isMobileDevice();
  if (mobile) {
    if (zoom >= 15) return 7;
    if (16 > zoom && zoom >= 12) return 6;
    if (12 > zoom && zoom >= 10) return 5;
    if (10 > zoom && zoom >= 8) return 4;
    if (8 > zoom) return 3;
    return 5; // Varsayılan
  } else {
    if (zoom >= 16) return 7;
    if (16 > zoom && zoom >= 13) return 6;
    if (13 > zoom && zoom >= 11) return 5;
    if (11 > zoom && zoom >= 8) return 4;
    if (8 > zoom) return 3;
    return 5; // Varsayılan
  }
};

/**
 * Bir seviye üst precision'a ait geohash'i döndürür.
 * @param {string} geohash - İşlenecek geohash
 * @returns {string} - Bir seviye üstteki parent geohash
 */
export const getParentGeohash = (geohash) => {
  // Özel durum: -L veya -R ile biten hash'ler için
  if (geohash.endsWith("-L") || geohash.endsWith("-R")) {
    return geohash.slice(0, -3);
  }
  // Normal geohash'ler için bir karakter kısalt
  return geohash.slice(0, -1);
};

/**
 * Geohash'i ikiye böler (sol ve sağ olarak).
 * @param {string} hash - Bölünecek geohash
 * @param {Object} hashBounds - Geohash'in sınırları
 * @returns {Array<Object>} - Bölünmüş geohash'ler dizisi
 */
export const splitGeohash = (hash, hashBounds) => {
  // Geohash'in genişliği ve yüksekliği
  const hashWidth = hashBounds.ne.lon - hashBounds.sw.lon;

  // Orta noktayı hesapla (yatay olarak böl)
  const middleLon = hashBounds.sw.lon + hashWidth / 2;

  // Sol parça (Left)
  const leftBounds = {
    sw: { lat: hashBounds.sw.lat, lon: hashBounds.sw.lon },
    ne: { lat: hashBounds.ne.lat, lon: middleLon },
  };

  // Sağ parça (Right)
  const rightBounds = {
    sw: { lat: hashBounds.sw.lat, lon: middleLon },
    ne: { lat: hashBounds.ne.lat, lon: hashBounds.ne.lon },
  };

  return [
    {
      hash: `${hash}-L`,
      bounds: [
        [leftBounds.sw.lat, leftBounds.sw.lon],
        [leftBounds.ne.lat, leftBounds.ne.lon],
      ],
    },
    {
      hash: `${hash}-R`,
      bounds: [
        [rightBounds.sw.lat, rightBounds.sw.lon],
        [rightBounds.ne.lat, rightBounds.ne.lon],
      ],
    },
  ];
};

/**
 * Görünür harita alanı için geohash'leri hesaplar ve parent geohash'leri bulur.
 * @param {Object} bounds - Harita sınırları
 * @param {number} precision - Geohash precision değeri
 * @param {number} bufferFactor - Görünür alan genişletme faktörü
 * @returns {Object} - Hesaplanan geohash'ler ve parent geohash'ler
 */
export const calculateVisibleGeohashes = (
  bounds,
  precision,
  bufferFactor = 0.1,
) => {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  // Görünür alanı genişlet
  const latRange = ne.lat - sw.lat;
  const lngRange = ne.lng - sw.lng;

  // Genişletilmiş sınırlar
  const extendedSouth = sw.lat - latRange * bufferFactor;
  const extendedNorth = ne.lat + latRange * bufferFactor;
  const extendedWest = sw.lng - lngRange * bufferFactor;
  const extendedEast = ne.lng + lngRange * bufferFactor;

  // Seçilen precision için Geohash boyutları hesaplanacak
  const geohashes = [];
  const hashSet = new Set();
  const parentGeohashSet = new Set(); // Bir alt precision seviyesindeki geohash'leri tutacak Set

  // Görünür alanın kapsadığı geohash'leri hesapla
  let north = extendedNorth;
  let south = extendedSouth;
  let east = extendedEast;
  let west = extendedWest;

  // İlk Geohash'i oluştur (kuzeybatı köşesi)
  const topLeftHash = Geohash.encode(north, west, precision);
  const topLeftBounds = Geohash.bounds(topLeftHash);

  // Geohash genişliği ve yüksekliği
  const hashWidth = topLeftBounds.ne.lon - topLeftBounds.sw.lon;
  const hashHeight = topLeftBounds.ne.lat - topLeftBounds.sw.lat;

  // Harita sınırları içindeki satır ve sütun sayısını hesapla
  const numCols = Math.ceil((east - west) / hashWidth) + 1;
  const numRows = Math.ceil((north - south) / hashHeight) + 1;

  // Görünür sınırlar içindeki tüm Geohash'leri oluştur
  for (let row = 0; row < numRows; row++) {
    for (let col = 0; col < numCols; col++) {
      // Bu hücrenin merkez koordinatlarını hesapla
      const lat = north - (row * hashHeight + hashHeight / 2);
      const lng = west + (col * hashWidth + hashWidth / 2);

      // Bu merkez için Geohash'i hesapla
      const hash = Geohash.encode(lat, lng, precision);

      // Eğer bu Geohash'i daha önce eklemediysen ekle
      if (!hashSet.has(hash)) {
        hashSet.add(hash);
        const hashBounds = Geohash.bounds(hash);

        // Precision 6 veya 4 ise geohash'i ikiye böl, değilse normal ekle
        if (precision === 6 || precision === 4) {
          const splitHashes = splitGeohash(hash, hashBounds);
          geohashes.push(...splitHashes);

          // Her splitHash için parent geohash'i ekle
          splitHashes.forEach((item) => {
            const parentGeohash = getParentGeohash(item.hash);
            parentGeohashSet.add(parentGeohash);
          });
        } else {
          geohashes.push({
            hash,
            bounds: [
              [hashBounds.sw.lat, hashBounds.sw.lon],
              [hashBounds.ne.lat, hashBounds.ne.lon],
            ],
            precision,
          });

          // Bir alt precision seviyesindeki parent geohash'i al
          const parentGeohash = getParentGeohash(hash);
          parentGeohashSet.add(parentGeohash);
        }
      }
    }
  }

  //console.log(
  //  `Toplam ${geohashes.length} geohash hesaplandı (precision: ${precision})`
  //);
  //console.log(
  //  `Parent geohash'ler (${parentGeohashSet.length}):`,
  //  parentGeohashSet
  //);

  return {
    geohashes,
    parentGeohashes: Array.from(parentGeohashSet),
  };
};
